import { useRef, useEffect, useCallback, useState } from 'react';

interface LiveTranscriptBlock {
  id: string;
  content: string;
  speakerLabel?: string;
  speakerId?: string;
  internalName?: string;
  timestamp: Date | number;
  isPartial: boolean;
}

export const useScrollToBottom = (blocks: LiveTranscriptBlock[]) => {
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new blocks are added if the user isn't manually scrolling
  useEffect(() => {
    if (transcriptContainerRef.current && blocks.length > 0 && !isUserScrolling) {
      const container = transcriptContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [blocks, isUserScrolling]);

  // Handle scroll events to detect if user is manually scrolling
  const handleScroll = useCallback(() => {
    if (!transcriptContainerRef.current) return;

    const container = transcriptContainerRef.current;
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50; // 50px tolerance

    setIsUserScrolling(!isAtBottom);
  }, []);

  // Function to scroll to bottom manually
  const scrollToBottom = useCallback(() => {
    if (transcriptContainerRef.current) {
      const container = transcriptContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
      setIsUserScrolling(false);
    }
  }, []);

  // Function to scroll to a specific transcript block
  const scrollToTranscriptBlock = useCallback(
    (blockId: string) => {
      // Find the transcript block that corresponds to this block
      const targetBlock = blocks.find((block) => block.id === blockId);
      if (!targetBlock || !transcriptContainerRef.current) return;

      // Find the DOM element for this block
      const blockElement = document.querySelector(`[data-segment-id="${blockId}"]`);
      if (blockElement) {
        // Scroll to the element
        blockElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Highlight the block temporarily
        const highlightClasses = ['border-sky-300'];
        blockElement.classList.add(...highlightClasses);
        setTimeout(() => {
          blockElement.classList.remove(...highlightClasses);
        }, 1000);

        setIsUserScrolling(true);
      }
    },
    [blocks],
  );

  return {
    transcriptContainerRef,
    isUserScrolling,
    handleScroll,
    scrollToBottom,
    scrollToTranscriptBlock,
  };
};
